import type { ErrorEvent, EventHint } from '@sentry/react'
import { isAxiosError } from 'axios'
import {
  confirmAdminBuildStale,
  isChunkLoadError,
  isChunkLoadErrorMessage,
} from '@/lib/admin-build'
import {
  hasLoggedOutRedirectStarted,
  isExpectedSessionExpirationError,
} from '@/lib/auth-response-handler'
import { annotateBrowserTranslationError } from '@/lib/browser-translation'
import { getErrorCauseChain } from '@/lib/error-cause-chain'

// Axios codes for requests that never received an HTTP response (connection drop, timeout, abort).
const NO_RESPONSE_AXIOS_CODES = new Set(['ERR_NETWORK', 'ECONNABORTED', 'ERR_CANCELED'])

function isChunkLoadEvent(event: ErrorEvent, hint: EventHint): boolean {
  return (
    isChunkLoadError(hint?.originalException) ||
    Boolean(
      event.exception?.values?.some((exception) => isChunkLoadErrorMessage(exception.value ?? ''))
    )
  )
}

function isNoResponseNetworkError(error: unknown): boolean {
  return getErrorCauseChain(error).some(
    (currentError) =>
      isAxiosError(currentError) &&
      !currentError.response &&
      NO_RESPONSE_AXIOS_CODES.has(currentError.code ?? '')
  )
}

/**
 * The one place that decides which admin browser errors are reportable. Expected conditions are
 * dropped or grouped into one warning-level issue that never pages; everything else reaches Sentry unchanged.
 */
export function beforeSendAdminEvent(
  event: ErrorEvent,
  hint: EventHint
): ErrorEvent | null | PromiseLike<ErrorEvent | null> {
  // The page is already unloading to /login; later failures are teardown noise.
  if (hasLoggedOutRedirectStarted()) {
    return null
  }

  if (isExpectedSessionExpirationError(hint?.originalException)) {
    return null
  }

  if (isChunkLoadEvent(event, hint)) {
    // Wait for admin-build.json before deciding: a stale tab's first failure can arrive before the
    // periodic check noticed the deploy. A replaced build is dropped (the refresh toast covers it);
    // a failure on the current build is one warning issue that escalates if a broken deploy spikes.
    return confirmAdminBuildStale().then((isStale) =>
      isStale ? null : { ...event, level: 'warning', fingerprint: ['admin-chunk-load-failure'] }
    )
  }

  if (isNoResponseNetworkError(hint?.originalException)) {
    return { ...event, level: 'warning', fingerprint: ['admin-network-error'] }
  }

  return annotateBrowserTranslationError(event, hint)
}
